import React, { useState } from "react";
import { MenuItemPerformance } from "@/redux/services/dashboard/dashboard.type";
import { formatPrice, cn } from "@/lib/utils";
import { getGrowthIndicator } from "@/lib/growthIndicator";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TopSellingItemsModal } from "./TopSellingItemsModal";
import {
  dashboardColors,
  dashboardShadows,
  dashboardSpacing,
} from "@/lib/theme";
import { useRestaurantCurrency } from "@/lib/hooks";

interface TopSellingItemsProps {
  items: MenuItemPerformance[];
  previousItems?: MenuItemPerformance[];
  loading?: boolean;
  type?: "top" | "least" | "combo";
}

export const TopSellingItems: React.FC<TopSellingItemsProps> = ({
  items,
  previousItems,
  loading,
  type = "top",
}) => {
  const [showModal, setShowModal] = useState(false);
  const currency = useRestaurantCurrency();
  if (loading) {
    return (
      <div
        className="rounded-lg"
        style={{
          backgroundColor: dashboardColors.bgCard,
          padding: dashboardSpacing.cardPadding,
          boxShadow: dashboardShadows.card,
        }}
      >
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }


  return (
    <div
      className="rounded-lg"
      style={{
        backgroundColor: dashboardColors.bgCard,
        padding: dashboardSpacing.cardPadding,
        boxShadow: dashboardShadows.card,
      }}
    >
      <h3
        className="text-lg font-semibold mb-4"
        style={{ color: dashboardColors.textPrimary }}
      >
        {type === "top"
          ? "Articles les plus vendus"
          : type === "combo"
          ? "Combos les plus vendus"
          : "Articles les moins vendus"}
      </h3>
      <div className="space-y-2">
        {items.slice(0, 5).map((item, index) => {
          const previousItem = previousItems?.find((p) => p.name === item.name);
          const isEven = index % 2 === 0;

          return (
            <div
              key={index}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg transition-all",
                isEven ? "bg-gray-50" : "bg-white",
                "hover:shadow-sm"
              )}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{
                    backgroundColor:
                      type === "top"
                        ? dashboardColors.primary
                        : type === "combo"
                        ? dashboardColors.primaryHover
                        : dashboardColors.warning,
                  }}
                >
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {item.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {item.orders} commandes • {formatPrice(item.revenue / 100, currency)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">
                  {item.percentage_of_total.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500">des ventes</p>
                {getGrowthIndicator(item, previousItem)}
              </div>
            </div>
          );
        })}
      </div>

      {items.length > 5 && (
        <Button
          variant="ghost"
          className="w-full mt-4 justify-between hover:bg-orange-50"
          onClick={() => setShowModal(true)}
          style={{
            color: dashboardColors.primary,
            borderColor: dashboardColors.border,
          }}
        >
          <span>Voir plus ({items.length - 5} de plus)</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}

      <TopSellingItemsModal
        open={showModal}
        onOpenChange={setShowModal}
        items={items}
        previousItems={previousItems}
        type={type}
      />
    </div>
  );
};
